import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useLocation, useSearch } from "wouter";
import { useGetWorkspaceCompanies } from "@workspace/api-client-react";
import { useMicroappContext } from "@/framework/microapp-context";

type WorkspaceCompanyScope = {
  companyId: number;
  companyName: string;
  companies: { companyId: number; name: string }[];
  isLoadingCompanies: boolean;
  isCompaniesError: boolean;
  setCompanyId: (companyId: number) => void;
};

const WorkspaceCompanyContext = createContext<WorkspaceCompanyScope | null>(null);

function parseCompanyId(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
}

export function WorkspaceCompanyProvider({ children }: { children: ReactNode }) {
  const { embedded, host } = useMicroappContext();
  const searchString = useSearch();
  const [pathname, setLocation] = useLocation();
  const params = useMemo(() => new URLSearchParams(searchString), [searchString]);
  const {
    data: workspaceCompanies,
    isLoading: isLoadingCompanies,
    isError: isCompaniesError,
  } = useGetWorkspaceCompanies();

  const companies = workspaceCompanies?.companies ?? [];
  const urlCompanyId = parseCompanyId(params.get("companyId"));
  const hostCompanyId = embedded && host ? host.businessContext.companyId : null;
  const defaultCompanyId =
    hostCompanyId
    ?? workspaceCompanies?.defaultCompanyId
    ?? companies[0]?.companyId
    ?? null;

  const resolvedCompanyId =
    (urlCompanyId && companies.some((company) => company.companyId === urlCompanyId)
      ? urlCompanyId
      : null)
    ?? (defaultCompanyId && companies.some((company) => company.companyId === defaultCompanyId)
      ? defaultCompanyId
      : null)
    ?? companies[0]?.companyId
    ?? defaultCompanyId
    ?? 0;

  const companyName =
    companies.find((company) => company.companyId === resolvedCompanyId)?.name
    ?? (resolvedCompanyId ? `Company ${resolvedCompanyId}` : "Company");

  useLayoutEffect(() => {
    if (!resolvedCompanyId || urlCompanyId === resolvedCompanyId) return;
    const next = new URLSearchParams(searchString);
    next.set("companyId", String(resolvedCompanyId));
    setLocation(`${pathname}?${next.toString()}`, { replace: true });
  }, [pathname, resolvedCompanyId, searchString, setLocation, urlCompanyId]);

  const setCompanyId = useCallback((nextCompanyId: number) => {
    const next = new URLSearchParams(searchString);
    next.set("companyId", String(nextCompanyId));
    next.delete("department");
    setLocation(`${pathname}?${next.toString()}`);
  }, [pathname, searchString, setLocation]);

  const value = useMemo(
    () => ({
      companyId: resolvedCompanyId,
      companyName,
      companies,
      isLoadingCompanies,
      isCompaniesError,
      setCompanyId,
    }),
    [
      companies,
      companyName,
      isCompaniesError,
      isLoadingCompanies,
      resolvedCompanyId,
      setCompanyId,
    ],
  );

  return (
    <WorkspaceCompanyContext.Provider value={value}>
      {children}
    </WorkspaceCompanyContext.Provider>
  );
}

export function useWorkspaceCompany(): WorkspaceCompanyScope {
  const context = useContext(WorkspaceCompanyContext);
  if (!context) {
    throw new Error("useWorkspaceCompany must be used within WorkspaceCompanyProvider");
  }
  return context;
}
